import React, { useState, useCallback, useEffect, useRef } from 'react';
import { optimizeKontentImageUrl } from '../DataUtils/kontentImageOptimization';
//@ts-ignore
import * as styles from './art-directed-image.module.css';

export interface ArtDirectedSource {
    url: string;
    media: string;
    width: number;
    height: number;
    fit?: string;
    quality?: number;
    sizes?: string;
}

interface Props {
    sources: ArtDirectedSource[];
    fallback: { url: string; width: number; height: number; quality?: number };
    alt: string;
    loading?: 'eager' | 'lazy';
    fetchPriority?: 'high' | 'low' | 'auto';
    className?: string;
    imgClassName?: string;
    style?: React.CSSProperties;
    imgStyle?: React.CSSProperties;
    elementCodename?: string;
}

function buildKontentUrl(
    baseUrl: string,
    width: number,
    height: number,
    fit: string = 'crop',
    quality: number = 90,
    format: 'webp' | 'jpg' = 'webp'
): string {
    return optimizeKontentImageUrl(baseUrl, {
        w: width,
        h: height,
        fit: fit as 'crop',
        q: quality,
        fm: format,
        auto: 'format',
    });
}

export function buildSrcSet(
    baseUrl: string,
    width: number,
    height: number,
    fit: string = 'crop',
    quality: number = 90
): string {
    const aspectRatio = height / width;
    const widths = [width * 0.25, width * 0.5, width * 0.75, width]
        .map(Math.round)
        .filter((w) => w > 0 && w <= 3840);
    return widths
        .map((w) => {
            const h = Math.round(w * aspectRatio);
            const url = buildKontentUrl(baseUrl, w, h, fit, quality, 'webp');
            return `${url} ${w}w`;
        })
        .join(', ');
}

const ArtDirectedImage: React.FC<Props> = ({
    sources,
    fallback,
    alt,
    loading = 'lazy',
    fetchPriority = 'auto',
    className,
    imgClassName,
    style,
    imgStyle,
    elementCodename,
}) => {
    const isEager = loading === 'eager';
    const [loaded, setLoaded] = useState(isEager);
    const onLoad = useCallback(() => setLoaded(true), []);
    const imgRef = useRef<HTMLImageElement>(null);

    // A cached image can finish before React attaches onLoad during
    // hydration; without this the blurred placeholder never goes away.
    useEffect(() => {
        if (!isEager && imgRef.current?.complete) setLoaded(true);
    }, [isEager]);

    const placeholderUrl = !isEager
        ? buildKontentUrl(
              fallback.url,
              20,
              Math.round(20 * (fallback.height / fallback.width)),
              'crop',
              10,
              'jpg'
          )
        : '';

    return (
        <div
            className={`${styles.wrapper} gatsby-image-wrapper ${
                className || ''
            }`}
            style={style}
            {...(elementCodename
                ? { 'data-kontent-element-codename': elementCodename }
                : {})}
        >
            {!isEager && (
                <img
                    src={placeholderUrl}
                    alt=""
                    aria-hidden="true"
                    width={fallback.width}
                    height={fallback.height}
                    className={`${styles.placeholder} ${
                        loaded ? styles.placeholderHidden : ''
                    }`}
                />
            )}
            <picture className={styles.picture}>
                {sources.map((source, idx) => (
                    <source
                        key={idx}
                        media={source.media}
                        srcSet={buildSrcSet(
                            source.url,
                            source.width,
                            source.height,
                            source.fit || 'crop',
                            source.quality || 90
                        )}
                        sizes={source.sizes || '100vw'}
                        type="image/webp"
                    />
                ))}
                <img
                    ref={imgRef}
                    src={buildKontentUrl(
                        fallback.url,
                        fallback.width,
                        fallback.height,
                        'crop',
                        fallback.quality ?? sources[0]?.quality ?? 90
                    )}
                    alt={alt}
                    loading={loading}
                    // @ts-ignore
                    fetchpriority={fetchPriority}
                    onLoad={!isEager ? onLoad : undefined}
                    className={`${styles.image} ${imgClassName || ''}`}
                    style={imgStyle}
                    width={fallback.width}
                    height={fallback.height}
                    decoding={isEager ? 'auto' : 'async'}
                />
            </picture>
        </div>
    );
};

export default ArtDirectedImage;
